<script setup lang="ts">
import { useEventListener } from "@vueuse/core";
import { ref } from "vue";
import { getLegacyApp } from "../../core/legacy-bridge";
import { t } from "../../i18n";
import { appStore } from "../../stores/app";
import ErrorList from "../ErrorList.vue";
import MaterialSelector from "../materials/MaterialSelector.vue";
import ProductSelector from "../products/ProductSelector.vue";
import { resetProductSelector } from "../products/productSelection";

const viewport = ref<HTMLElement>();
const isMenuCollapsed = ref(false);
const activeTab = ref<"modules" | "fabrics">("modules");

function showTab(tab: "modules" | "fabrics") {
    activeTab.value = tab;
    if (tab === "modules") {
        resetProductSelector();
    }
}

// A click anywhere outside the 3D view cancels placing a module. Listens in the bubble phase on purpose:
// clicking a product stops its click from getting here, so starting a placement doesn't cancel itself.
useEventListener(document, "click", (e: MouseEvent) => {
    if (viewport.value && !viewport.value.contains(e.target as Node)) {
        getLegacyApp().onClickOutsideViewport();
    }
});

// Configuration tiles: the clicked one is marked active straight away, then app.ts loads it
function selectConfiguration(id: string) {
    appStore.configurations.activeId = id;
    getLegacyApp().selectConfiguration(id);
}

function deleteConfiguration(id: string) {
    // Same order as before: the trash icon's click also selected its tile, then deleted it
    selectConfiguration(id);
    getLegacyApp().deleteConfiguration(id);
}
</script>

<!-- Shown when appStore.page is "viewport" (set by showPage in app.ts) -->
<template>
    <div class="viewport-page page" data-page="viewport" :class="{ 'is-shown': appStore.page === 'viewport' }">
        <div class="page__content page__content--viewport">
            <div ref="viewport" class="viewport" :class="{ 'is-loading': appStore.loadingCanvas, 'is-menu-collapsed': isMenuCollapsed }">
                <!-- Babylon renders into #renderCanvas (app.ts); #renderCanvasDrop is the drop target for products -->
                <canvas id="renderCanvas"></canvas>
                <canvas id="loadCanvas" :class="{ 'is-active': appStore.loadingCanvas }"></canvas>
                <div id="renderCanvasDrop"></div>
                <ErrorList />
            </div>
        </div>

        <div id="menu" class="config-menu" :class="{ 'is-collapsed': isMenuCollapsed }">
            <div class="config-menu__header">
                <a class="config-menu__close" @click="isMenuCollapsed = !isMenuCollapsed"><span v-html="t('page.viewport.menu')"></span></a>
                <a class="config-menu__customize" @click.prevent="appStore.modal = 'options'"><span v-html="t('page.viewport.customize')"></span></a>
            </div>

            <div class="config-menu__sections">
                <div class="config-menu__section config-menu__section--configurations">
                    <div class="config-menu__section__header bulma-contextual">
                        <div class="tabs is-boxed is-medium is-fullwidth is-left is-disabled">
                            <ul>
                                <li class="is-active">
                                    <a><span v-html="t('page.viewport.configurations')"></span></a>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="config-menu__section__content">
                        <div class="configuration-selector">
                            <ul>
                                <li v-for="configuration in appStore.configurations.items" :key="configuration.id"
                                    :class="{ 'is-active': configuration.id === appStore.configurations.activeId }" :data-id="configuration.id"
                                    @click="selectConfiguration(configuration.id)">
                                    <div>
                                        <span :style="{ backgroundImage: `url('${configuration.thumbnail}')` }">
                                            <a class="configuratior-selector__delete" @click.stop="deleteConfiguration(configuration.id)"><i class="fas fa-trash-can"></i></a>
                                        </span>
                                    </div>
                                </li>
                                <li v-if="appStore.configurations.canAdd" class="is-new" @click="getLegacyApp().newConfiguration()">
                                    <div>
                                        <span><i class="fas fa-plus-circle fa-2x"></i></span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div class="config-menu__section config-menu__section--menues">
                    <div class="config-menu__section__header bulma-contextual">
                        <div class="tabs is-boxed is-medium config-menu__submenu-selection">
                            <ul>
                                <li data-menu="modules" :class="{ 'is-active': activeTab === 'modules' }" @click="showTab('modules')">
                                    <a>
                                        <span v-html="t('page.viewport.modules')"></span>
                                    </a>
                                </li>
                                <li data-menu="fabrics" :class="{ 'is-active': activeTab === 'fabrics' }" @click="showTab('fabrics')">
                                    <a>
                                        <span v-html="t('page.viewport.fabrics')"></span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="config-menu__section__content">
                        <div class="config-menu__submenues">
                            <div class="config-menu__submenues__submenu" :class="{ 'is-shown': activeTab === 'modules' }" data-menuview="modules">
                                <ProductSelector />
                            </div>
                            <div class="config-menu__submenues__submenu" :class="{ 'is-shown': activeTab === 'fabrics' }" data-menuview="fabrics">
                                <MaterialSelector />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
